import {
  columnFilteringFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  constructSortFn,
  createExpandedRowModel,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_includesString,
  globalFilteringFeature,
  metaHelper,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  tableFeatures,
} from "@tanstack/react-table-v9";

import { toNumber } from "./numbers";
import type { TableV2ColumnMeta } from "./types";

const normalizeSortValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.length;
  if (typeof value === "string") return value.toUpperCase();
  return value;
};

export const sortFn_caseInsensitive = constructSortFn({
  sort: (a, b) => {
    const left = normalizeSortValue(a);
    const right = normalizeSortValue(b);
    if (left === right) return 0;
    if (left === null || left === undefined) return 1;
    if (right === null || right === undefined) return -1;
    return (left as number | string) > (right as number | string) ? 1 : -1;
  },
});

const isEmptySortValue = (value: unknown) =>
  value === null || value === undefined || value === "";

/** Compares `toNumber`/`toTime` values, with empty or unparseable ones last */
const constructParsedSortFn = (parse: (value: unknown) => number | null) =>
  constructSortFn({
    sort: (a, b) => {
      const left = isEmptySortValue(a) ? null : parse(a);
      const right = isEmptySortValue(b) ? null : parse(b);
      if (left === right) return 0;
      if (left === null) return 1;
      if (right === null) return -1;
      return left > right ? 1 : -1;
    },
  });

const toTime = (value: unknown): number | null => {
  const time =
    value instanceof Date ? value.getTime() : Date.parse(String(value));
  return Number.isNaN(time) ? null : time;
};

export const sortFn_numeric = constructParsedSortFn(toNumber);
export const sortFn_date = constructParsedSortFn(toTime);

export const tableV2Features = tableFeatures({
  columnVisibilityFeature,
  columnOrderingFeature,
  columnSizingFeature,
  columnResizingFeature,
  columnPinningFeature,
  columnFilteringFeature,
  globalFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
  filterFns: { includesString: filterFn_includesString },
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: {
    caseInsensitive: sortFn_caseInsensitive,
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    numeric: sortFn_numeric,
    date: sortFn_date,
  },
  rowExpandingFeature,
  expandedRowModel: createExpandedRowModel(),
  rowPaginationFeature,
  paginatedRowModel: createPaginatedRowModel(),
  rowSelectionFeature,
  columnMeta: metaHelper<TableV2ColumnMeta>(),
});

export type TableV2Features = typeof tableV2Features;
